import { apiClient, baseUrl } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Naklady na inzerci (krok O3): typovany klient nad
 * `/v1/finance/advertising-invoices*` a `/v1/finance/advertising-costs`.
 *
 * Odpovedi finance rout jsou `zod.any()` (viz packages/finance/src/public/plugin.ts),
 * takze DTO typy jsou tu psane rucne podle advertising-service.ts; request body
 * a query typy chodi primo z generovaneho OpenAPI kontraktu.
 */

export type AdvertisingInvoiceStatus = 'unallocated' | 'proposed' | 'approved';
export type AllocationMethod = 'auto_by_publications' | 'manual';

export interface AdvertisingAllocationDto {
  readonly id: string;
  readonly setSeq: number;
  readonly kind: 'proposal' | 'approval';
  readonly propertyId: string;
  readonly listingId: string | null;
  readonly amount: string;
  readonly currency: string;
  readonly method: AllocationMethod;
  readonly daysAdvertised: number | null;
  readonly propertyCostId: string | null;
  readonly createdAt: string;
}

export interface AdvertisingInvoiceDto {
  readonly id: string;
  readonly portal: string;
  readonly periodFrom: string;
  readonly periodTo: string;
  readonly amount: string;
  readonly currency: string;
  readonly vatRate: string | null;
  readonly documentId: string | null;
  readonly status: AdvertisingInvoiceStatus;
  readonly statusReason: string | null;
  readonly note: string | null;
  readonly approvedAt: string | null;
  readonly createdAt: string;
  readonly allocations: AdvertisingAllocationDto[];
}

export interface AdvertisingCostsCell {
  readonly month: string;
  readonly portal: string;
  readonly propertyId: string;
  readonly currency: string;
  readonly total: string;
}

export interface AdvertisingCostsSummaryRow {
  readonly key: string;
  readonly currency: string;
  readonly total: string;
}

export interface AdvertisingCostsResult {
  readonly groupBy: AdvertisingCostsGroupBy;
  readonly cells: AdvertisingCostsCell[];
  readonly summary: AdvertisingCostsSummaryRow[];
}

export type CreateAdvertisingInvoiceBody =
  ApiPaths['/v1/finance/advertising-invoices']['post']['requestBody']['content']['application/json'];
export type AdvertisingCostsGroupBy =
  ApiPaths['/v1/finance/advertising-costs']['get']['parameters']['query']['groupBy'];

export async function listAdvertisingInvoices(status?: AdvertisingInvoiceStatus): Promise<AdvertisingInvoiceDto[]> {
  const result = await unwrap(
    apiClient.GET('/v1/finance/advertising-invoices', {
      params: { query: status === undefined ? {} : { status } },
    }),
  );
  return (result as { items: AdvertisingInvoiceDto[] }).items;
}

export async function createAdvertisingInvoice(body: CreateAdvertisingInvoiceBody): Promise<AdvertisingInvoiceDto> {
  return (await unwrap(apiClient.POST('/v1/finance/advertising-invoices', { body }))) as AdvertisingInvoiceDto;
}

export async function proposeAdvertisingAllocation(id: string): Promise<AdvertisingInvoiceDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/advertising-invoices/{id}/propose', { params: { path: { id } } }),
  )) as AdvertisingInvoiceDto;
}

export async function approveAdvertisingAllocation(
  id: string,
  allocations: readonly { propertyId: string; amount: string }[],
): Promise<AdvertisingInvoiceDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/advertising-invoices/{id}/approve', {
      params: { path: { id } },
      body: { allocations: [...allocations] },
    }),
  )) as AdvertisingInvoiceDto;
}

export interface AdvertisingCostsParams {
  readonly groupBy: AdvertisingCostsGroupBy;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

export async function getAdvertisingCosts(params: AdvertisingCostsParams): Promise<AdvertisingCostsResult> {
  return (await unwrap(
    apiClient.GET('/v1/finance/advertising-costs', {
      params: {
        query: {
          groupBy: params.groupBy,
          ...(params.from !== undefined ? { from: params.from } : {}),
          ...(params.to !== undefined ? { to: params.to } : {}),
        },
      },
    }),
  )) as AdvertisingCostsResult;
}

/** Stejny vzor jako costsSummaryCsvUrl - CSV se stahuje odkazem, ne fetchem. */
export function advertisingCostsCsvUrl(params: AdvertisingCostsParams): string {
  const search = new URLSearchParams({ groupBy: params.groupBy, format: 'csv' });
  if (params.from !== undefined) search.set('from', params.from);
  if (params.to !== undefined) search.set('to', params.to);
  return `${baseUrl}/v1/finance/advertising-costs?${search.toString()}`;
}
